import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card, Spinner } from '@easydent/shared-ui';
import { getDashboard, getIncompleteCustomers, type Dashboard, type IncompleteCustomer } from '@/lib/api';

const Kpi: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <Card>
    <div className="text-xs uppercase tracking-wide text-subtle-ink">{label}</div>
    <div className={`mt-1 text-2xl font-bold ${accent ? 'text-brand-ink-strong' : 'text-body'}`}>{value}</div>
  </Card>
);

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [d, setD] = useState<Dashboard | null>(null);
  const [incomplete, setIncomplete] = useState<IncompleteCustomer[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDashboard().then(setD).catch(() => setError(t('dashboard.loadError', "Could not load the dashboard.")));
    getIncompleteCustomers().then(setIncomplete).catch(() => {});
  }, []);

  const money = (n: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: d?.currency ?? 'EUR', maximumFractionDigits: 0 }).format(n);

  if (error) return <Alert variant="error">{error}</Alert>;
  if (!d) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-body">{t('dashboard.title', "Platform overview")}</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={t('dashboard.mrr', "Estimated MRR")} value={money(d.estimatedMrr)} accent />
        <Kpi label={t('dashboard.tenants', "Tenants")} value={String(d.tenantCount)} />
        <Kpi label={t('dashboard.active', "Active")} value={String(d.activeCount)} />
        <Kpi label={t('dashboard.trialing', "Trialing")} value={String(d.trialingCount)} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={t('dashboard.pastDue', "Past due")} value={String(d.pastDueCount)} />
        <Kpi label={t('dashboard.cabinets', "Cabinets")} value={String(d.totalCabinets)} />
        <Kpi label={t('dashboard.users', "Users")} value={String(d.totalUsers)} />
        <Kpi label={t('dashboard.patients', "Patients")} value={String(d.totalPatients)} />
      </div>

      <Card>
        <div className="mb-2 text-sm font-semibold text-body-strong">{t('dashboard.tenantsByStatus', "Tenants by status")}</div>
        {d.byStatus.length === 0 ? (
          <p className="text-sm text-muted">{t('dashboard.noTenants', "No tenants yet.")}</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {d.byStatus.map((s) => (
              <li key={s.status} className="flex items-center justify-between py-2 text-sm">
                <span className="text-body-soft">{s.status}</span>
                <span className="font-medium text-body">{s.count}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Shown only when there is something to do. Nobody needs a permanent empty panel telling
          them everything is fine. */}
      {incomplete.length > 0 && (
        <Card>
          <div className="mb-1 text-sm font-semibold text-body-strong">{t('dashboard.notInvoiceable', "Cannot be invoiced yet")}</div>
          <p className="mb-2 text-xs text-muted">
            Missing company details. Those who already owe money are listed first — an invoice we
            cannot issue is worse than one we do not yet need.
          </p>
          <ul className="divide-y divide-line-soft">
            {incomplete.map((c) => (
              <li key={`${c.customerType}:${c.customerId}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <div>
                  <div className="text-body-strong">
                    {c.name}
                    <span className="ml-2 rounded bg-subtle px-1.5 py-0.5 text-[11px] text-muted">
                      {c.customerType}
                    </span>
                    {c.hasBalance && (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800">
                        money owed
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-subtle-ink">
                    missing: {c.missingFields.join(', ')}
                    {c.email && <> · {c.email}</>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
